import { useState, type FormEvent, type ReactElement } from "react";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { authErrorMessage } from "@shared/authErrors";
import { AuthShell } from "@/components/layout/AuthShell";
import { FormInput } from "@/components/ui/FormInput";
import { PasswordField } from "@/components/ui/PasswordField";
import { MIN_PASSWORD_SCORE, scorePassword } from "@/lib/passwordStrength";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";

/**
 * Makes a buyer replace the starter password we emailed them before their
 * purchases hub opens. `must_set_password` is stamped on the auth user when
 * settlement provisioned the account, and cleared here — it rides in user
 * metadata precisely so this needs no schema of its own.
 *
 * Only the hub is gated. The post-purchase funnel (/access, /learn) is not: a
 * buyer who just paid gets their product straight away, and meets this screen the
 * first time they come back to the dashboard — which is the moment the emailed
 * password is worth trading in.
 */
export function SetPasswordGate({ children }: { children: ReactElement }) {
  const { user } = useAuth();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // AuthContext deliberately keeps the previous session object when the signed-in
  // identity hasn't changed, so updateUser's fresh metadata never reaches it.
  // This local flag is what opens the gate for the rest of the visit.
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Kata laluan tidak sepadan");
      return;
    }
    setSaving(true);
    // Scored here rather than read off the meter's state: the meter's estimate
    // arrives asynchronously (zxcvbn is loaded on demand), so a buyer who types
    // fast and submits immediately would otherwise be told a strong password is
    // too weak.
    const { score } = await scorePassword(password, [user?.email ?? ""]);
    if (score < MIN_PASSWORD_SCORE) {
      setError("Kata laluan terlalu lemah — panjangkan atau campurkan perkataan/simbol lain");
      setSaving(false);
      return;
    }
    const { error: err } = await supabase.auth.updateUser({
      password,
      data: { must_set_password: false },
    });
    setSaving(false);
    if (err) {
      toast.error(authErrorMessage(err, err.message));
      return;
    }
    toast.success("Kata laluan anda dah ditetapkan.");
    setDone(true);
  }

  if (done || !user?.user_metadata?.must_set_password) return children;

  return (
    <AuthShell
      title="Tetapkan kata laluan anda"
      subtitle="Satu langkah terakhir sebelum akses produk anda"
    >
      <div className="mb-5 flex items-start gap-2 rounded-xl border border-brand-200 bg-brand-50/50 p-3">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
        <p className="text-sm text-slate-600">
          Anda masuk guna kata laluan sementara yang kami emelkan. Pilih kata laluan sendiri
          sekarang — yang ini je akan digunakan selepas ini.
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {/* Hidden username so the browser saves the new password against the right account. */}
        <input type="email" autoComplete="username" value={user.email ?? ""} readOnly hidden />
        <PasswordField
          label="Kata laluan baharu"
          autoComplete="new-password"
          showMeter
          userInputs={[user.email ?? ""]}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormInput
          label="Sahkan kata laluan"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={error}
        />
        <Button type="submit" className="w-full" loading={saving} disabled={!password || !confirm}>
          Simpan & teruskan
        </Button>
      </form>
    </AuthShell>
  );
}
